import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import {
  acquireMicStream,
  getStoredDeviceLabel,
} from 'dashboard/store/modules/phone';
import {
  MIC_VERDICTS,
  createMicLevelTracker,
  rmsDbfs,
} from 'dashboard/helper/micLevel';

const TEST_TONE_FREQUENCY = 440;
const TEST_TONE_DURATION = 2000;
const SAVED_BADGE_DURATION = 2500;
// ~23 ms of audio per reading: long enough for a steady RMS, short enough to
// follow speech.
const ANALYSER_FFT_SIZE = 1024;
// Chrome fires devicechange two to four times for one plug, and the first
// enumeration is still half-built (same reasoning as PhoneAudioElements).
const DEVICE_CHANGE_DEBOUNCE = 400;

// The connected devices of one kind, plus the remembered one when it is not
// among them. The store no longer forgets a device just because it is missing,
// so the picker shows it — flagged — instead of silently reading "System
// default" and making the choice look unsaved. With the ids still hidden
// (no mic permission yet) nothing can be called disconnected.
const withRememberedDevice = (devices, storedId, storedLabel) => {
  if (!storedId || devices.some(d => d.deviceId === storedId)) return devices;
  return [
    ...devices,
    {
      deviceId: storedId,
      label: storedLabel || '',
      disconnected: devices.length > 0,
    },
  ];
};

/**
 * Device picking + testing for the microphone and the speaker, shared by the
 * dashboard popup (PhoneAudioSettings) and the inline CTI panel
 * (CtiAudioSettings) - the Salesforce utility bar has no room for a modal.
 *
 * A pick is saved the moment it is made (there is no Save button — Done only
 * closes), so the saved state is confirmed inline via `savedDevice`.
 *
 * @param {Object} options
 * @param {Function} options.onError - called with a ready-to-show message when
 *   a test fails for a reason other than a denied permission (that one is
 *   surfaced through `microphonePermissionDenied` instead).
 *
 * The mic test runs only when the agent starts it: one left running unnoticed
 * holds a second capture of the mic into the next call.
 */
export function usePhoneAudioDevices({ onError = () => {} } = {}) {
  const store = useStore();
  const { t } = useI18n();

  const microphones = ref([]);
  const speakers = ref([]);
  const selectedMicrophone = ref('');
  const selectedSpeaker = ref('');
  const microphoneLevel = ref(0);
  // Band of the level right now (colours the bar) vs. the steady verdict over
  // the last few seconds (the sentence under it) — see createMicLevelTracker.
  const microphoneBand = ref(MIC_VERDICTS.SILENT);
  const microphoneVerdict = ref(MIC_VERDICTS.LISTENING);
  const speakerVolume = ref(0.5);
  const isTestingMicrophone = ref(false);
  const isTestingSpeaker = ref(false);
  const microphonePermissionDenied = ref(false);
  // The chosen output rejected setSinkId and the tone played on the system
  // default instead — shown inline so the test can't read as a pass.
  const speakerTestUsedDefault = ref(false);
  // 'microphone' | 'speaker' for a moment after a pick was saved.
  const savedDevice = ref(null);

  // Audio plumbing is deliberately kept out of the reactive graph: wrapping a
  // live AudioContext in a ref makes Vue walk its internals on every tick.
  let audioContext = null;
  let analyser = null;
  let microphone = null;
  let animationFrame = null;
  let oscillator = null;
  let speakerTestTimeout = null;
  let micPermissionStatus = null;
  let savedTimeout = null;
  let deviceChangeTimeout = null;
  let isStartingMicrophoneTest = false;
  // Set by cleanup(). onMounted awaits the device list before it builds the
  // audio graph, and a panel closed during that await must not get a context
  // and a devicechange listener that nothing would ever release.
  let isTornDown = false;

  const extensionAvailable = useMapGetter('phone/getChromeExtensionAvailable');
  // The test opens its own capture of the very mic the call is using. Some
  // headsets and drivers only serve one capture at a time and starve the older
  // one — the call's — so the call goes out silent. No test during a call.
  const session = useMapGetter('phone/getSession');
  const isCallActive = computed(() => Boolean(session.value));

  const permissionErrorMessage = computed(() =>
    // The extension is what grants mic access; if it's missing, tell the
    // operator that rather than pointing them at browser site settings.
    extensionAvailable.value
      ? t('PHONE_AUDIO.ERRORS.MICROPHONE_PERMISSION_DENIED')
      : t('PHONE_AUDIO.ERRORS.EXTENSION_REQUIRED')
  );

  const isSelectedDisconnected = (devices, selectedId) =>
    devices.some(d => d.deviceId === selectedId && d.disconnected);

  const microphoneDisconnected = computed(() =>
    isSelectedDisconnected(microphones.value, selectedMicrophone.value)
  );
  const speakerDisconnected = computed(() =>
    isSelectedDisconnected(speakers.value, selectedSpeaker.value)
  );

  const microphoneVerdictText = computed(
    () =>
      ({
        [MIC_VERDICTS.LISTENING]: t('PHONE_AUDIO.MICROPHONE.VERDICT.LISTENING'),
        [MIC_VERDICTS.SILENT]: t('PHONE_AUDIO.MICROPHONE.VERDICT.SILENT'),
        [MIC_VERDICTS.LOW]: t('PHONE_AUDIO.MICROPHONE.VERDICT.LOW'),
        [MIC_VERDICTS.GOOD]: t('PHONE_AUDIO.MICROPHONE.VERDICT.GOOD'),
      })[microphoneVerdict.value]
  );

  const optionLabel = (device, fallbackName) => {
    const name =
      device.label || `${fallbackName} ${device.deviceId.substring(0, 8)}`;
    return device.disconnected
      ? `${name} (${t('PHONE_AUDIO.DISCONNECTED_SUFFIX')})`
      : name;
  };
  const microphoneOptionLabel = device => optionLabel(device, 'Microphone');
  const speakerOptionLabel = device => optionLabel(device, 'Speaker');

  const loadAudioDevices = async () => {
    try {
      // Let the store re-find a remembered device whose id changed FIRST, so
      // the dropdowns below are a faithful projection of what a call would use.
      await store.dispatch('phone/validateAudioDevices');

      const devices = await navigator.mediaDevices.enumerateDevices();
      // Chrome returns one id-less, label-less placeholder per kind before mic
      // permission is granted; it can't be selected, so don't list it.
      const connected = kind =>
        devices.filter(device => device.kind === kind && device.deviceId);

      selectedMicrophone.value =
        store.getters['phone/getSelectedMicrophoneId'] || '';
      selectedSpeaker.value = store.getters['phone/getSelectedSpeakerId'] || '';

      microphones.value = withRememberedDevice(
        connected('audioinput'),
        selectedMicrophone.value,
        getStoredDeviceLabel('audioinput')
      );
      speakers.value = withRememberedDevice(
        connected('audiooutput'),
        selectedSpeaker.value,
        getStoredDeviceLabel('audiooutput')
      );
    } catch (error) {
      // No mediaDevices at all (blocked by permissions policy in an iframe) or
      // the enumeration failed - the inline permission notice covers it.
      microphonePermissionDenied.value = true;
    }
  };

  const flashSaved = kind => {
    // A pick saved after the panel closed must not leave a timer behind.
    if (isTornDown) return;
    savedDevice.value = kind;
    clearTimeout(savedTimeout);
    savedTimeout = setTimeout(() => {
      savedDevice.value = null;
      savedTimeout = null;
    }, SAVED_BADGE_DURATION);
  };

  // A disconnected entry is listed only while it is the choice.
  const dropUnchosenDisconnected = (devices, chosenId) =>
    devices.filter(d => !d.disconnected || d.deviceId === chosenId);

  const setupAudioContext = () => {
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      analyser = audioContext.createAnalyser();
      analyser.fftSize = ANALYSER_FFT_SIZE;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Unable to setup audio context: ', error);
    }
  };

  const watchMicrophonePermission = async () => {
    // Surface a blocked mic up front (and keep it in sync) so the operator
    // sees why the input is unusable without having to run the test first.
    if (!navigator.permissions?.query) return;
    try {
      const status = await navigator.permissions.query({ name: 'microphone' });
      micPermissionStatus = status;
      microphonePermissionDenied.value = status.state === 'denied';
      status.onchange = () => {
        microphonePermissionDenied.value = status.state === 'denied';
      };
    } catch {
      // Permissions API or the 'microphone' name is unsupported here; fall
      // back to detecting denial when the test runs.
    }
  };

  const monitorMicrophoneLevel = () => {
    const samples = new Float32Array(analyser.fftSize);
    let tracker = createMicLevelTracker(performance.now());

    const updateLevel = () => {
      if (!isTestingMicrophone.value || !analyser) return;

      const now = performance.now();
      if (audioContext?.state === 'running') {
        analyser.getFloatTimeDomainData(samples);
        const { percent, band, verdict } = tracker.push(rmsDbfs(samples), now);
        microphoneLevel.value = percent;
        microphoneBand.value = band;
        microphoneVerdict.value = verdict;
      } else {
        // A context held back by the autoplay policy feeds the analyser pure
        // silence. That says nothing about the mic, so don't judge it — and
        // start the warm-up over once audio actually flows.
        tracker = createMicLevelTracker(now);
        microphoneLevel.value = 0;
        microphoneBand.value = MIC_VERDICTS.SILENT;
        microphoneVerdict.value = MIC_VERDICTS.LISTENING;
      }

      animationFrame = requestAnimationFrame(updateLevel);
    };

    updateLevel();
  };

  const stopMicrophoneTest = () => {
    if (microphone) {
      microphone.mediaStream.getTracks().forEach(track => track.stop());
      microphone.disconnect();
      microphone = null;
    }
    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
      animationFrame = null;
    }
    isTestingMicrophone.value = false;
    microphoneLevel.value = 0;
    microphoneBand.value = MIC_VERDICTS.SILENT;
    microphoneVerdict.value = MIC_VERDICTS.LISTENING;
  };

  const testMicrophone = async () => {
    if (isTestingMicrophone.value) {
      stopMicrophoneTest();
      return;
    }
    // Torn down already, a start is in flight, or a call owns the mic.
    if (!audioContext || isStartingMicrophoneTest || isCallActive.value) return;

    isStartingMicrophoneTest = true;
    try {
      // Acquire exactly the way a call does (same constraints, same pin-drop,
      // same OverconstrainedError retry) so the level and its verdict are
      // evidence about the call and not about some other device.
      // The extension pre-grants this origin's mic permission, so the stream
      // request resolves without a prompt.
      const stream = await acquireMicStream();
      if (!audioContext || isCallActive.value) {
        stream.getTracks?.().forEach(track => track.stop());
        return;
      }
      microphonePermissionDenied.value = false;

      // Created without a fresh user gesture the context may start suspended.
      if (audioContext.state === 'suspended') {
        Promise.resolve(audioContext.resume?.()).catch(() => {});
      }

      microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyser);

      isTestingMicrophone.value = true;
      monitorMicrophoneLevel();
    } catch (error) {
      if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
        // Permission denied is already explained by the inline label.
        microphonePermissionDenied.value = true;
      } else {
        onError(t('PHONE_AUDIO.ERRORS.MICROPHONE_ACCESS'));
      }
    } finally {
      isStartingMicrophoneTest = false;
    }
  };

  // Persist the choice and apply it to the (pre)warmed mic / live call. A
  // running test moves over too — otherwise it keeps metering the old device
  // and the pick looks like it did nothing.
  const onMicrophoneChange = async deviceId => {
    const wasTesting = isTestingMicrophone.value;
    if (wasTesting) stopMicrophoneTest();

    await store.dispatch('phone/setMicrophoneDevice', deviceId);
    microphones.value = dropUnchosenDisconnected(microphones.value, deviceId);
    flashSaved('microphone');

    if (wasTesting) testMicrophone();
  };

  // Persist the choice; PhoneAudioElements applies setSinkId to call audio.
  const onSpeakerChange = async deviceId => {
    speakerTestUsedDefault.value = false;
    await store.dispatch('phone/setSpeakerDevice', deviceId);
    speakers.value = dropUnchosenDisconnected(speakers.value, deviceId);
    flashSaved('speaker');
  };

  const stopSpeakerTest = () => {
    if (oscillator) {
      oscillator.stop();
      oscillator.disconnect();
      oscillator = null;
    }
    if (speakerTestTimeout) {
      clearTimeout(speakerTestTimeout);
      speakerTestTimeout = null;
    }
    isTestingSpeaker.value = false;
  };

  const testSpeaker = async () => {
    if (isTestingSpeaker.value) {
      stopSpeakerTest();
      return;
    }

    speakerTestUsedDefault.value = false;
    try {
      // Route the test tone to the chosen output, matching real call audio.
      // AudioContext.setSinkId is Chrome-only; elsewhere it stays on default.
      if (typeof audioContext.setSinkId === 'function') {
        try {
          await audioContext.setSinkId(selectedSpeaker.value || '');
        } catch (sinkError) {
          // Falling back to the default output is fine, but reporting success
          // is not: a tone the agent can hear on their laptop speakers used to
          // "prove" an output device that call audio can't reach.
          speakerTestUsedDefault.value = Boolean(selectedSpeaker.value);
        }
      }

      oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(
        TEST_TONE_FREQUENCY,
        audioContext.currentTime
      );
      gainNode.gain.setValueAtTime(
        speakerVolume.value,
        audioContext.currentTime
      );

      isTestingSpeaker.value = true;
      oscillator.start();

      speakerTestTimeout = setTimeout(stopSpeakerTest, TEST_TONE_DURATION);
    } catch (error) {
      onError(t('PHONE_AUDIO.ERRORS.SPEAKER_TEST'));
    }
  };

  // Keep an open dialog in sync when a headset is plugged in or pulled out;
  // the repair itself lives in the store so it also runs with the dialog shut.
  // A running test is restarted only when it has to be: its device went away,
  // or the chosen mic is back while the test fell back to the default. Opening
  // a Bluetooth headset's mic itself fires devicechange (profile switch), so
  // restarting unconditionally would loop.
  const refreshAfterDeviceChange = async () => {
    await loadAudioDevices();
    if (!isTestingMicrophone.value) return;

    const track = microphone?.mediaStream?.getAudioTracks?.()[0];
    const capturingId = track?.getSettings?.().deviceId;
    const chosenId = selectedMicrophone.value;
    const chosenIsBack =
      chosenId &&
      capturingId &&
      capturingId !== chosenId &&
      microphones.value.some(m => m.deviceId === chosenId && !m.disconnected);

    if (track?.readyState === 'ended' || chosenIsBack) {
      stopMicrophoneTest();
      testMicrophone();
    }
  };

  const onDeviceChange = () => {
    clearTimeout(deviceChangeTimeout);
    deviceChangeTimeout = setTimeout(
      refreshAfterDeviceChange,
      DEVICE_CHANGE_DEBOUNCE
    );
  };

  const cleanup = () => {
    isTornDown = true;
    stopMicrophoneTest();
    stopSpeakerTest();
    clearTimeout(savedTimeout);
    clearTimeout(deviceChangeTimeout);
    navigator.mediaDevices?.removeEventListener?.(
      'devicechange',
      onDeviceChange
    );
    if (micPermissionStatus) {
      micPermissionStatus.onchange = null;
      micPermissionStatus = null;
    }
    // cleanup() can run twice (an explicit close then the unmount hook); null
    // the context and skip an already-closed one so close() doesn't reject
    // with "Cannot close a closed AudioContext".
    if (audioContext) {
      if (audioContext.state !== 'closed') {
        audioContext.close().catch(() => {});
      }
      audioContext = null;
    }
    analyser = null;
  };

  onMounted(async () => {
    // Make sure we know whether the companion extension is present so the
    // permission messaging above can point the operator at the right fix.
    store.dispatch('phone/checkChromeExtension');
    await loadAudioDevices();
    if (isTornDown) return;
    setupAudioContext();
    watchMicrophonePermission();
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
  });

  // A call that starts while the settings are open takes the mic over.
  watch(isCallActive, active => {
    if (active) stopMicrophoneTest();
  });

  onBeforeUnmount(cleanup);

  return {
    microphones,
    speakers,
    selectedMicrophone,
    selectedSpeaker,
    microphoneLevel,
    microphoneBand,
    microphoneVerdict,
    microphoneVerdictText,
    speakerVolume,
    isTestingMicrophone,
    isTestingSpeaker,
    isCallActive,
    microphonePermissionDenied,
    microphoneDisconnected,
    speakerDisconnected,
    speakerTestUsedDefault,
    savedDevice,
    permissionErrorMessage,
    microphoneOptionLabel,
    speakerOptionLabel,
    onMicrophoneChange,
    onSpeakerChange,
    testMicrophone,
    testSpeaker,
    cleanup,
  };
}

export default usePhoneAudioDevices;
